import { getPopupDetails } from '@Actions/popup.actions';
import {
	popupInspectionsLoadingSelector,
	popupSelectedAssetSelector,
} from '@Selectors/popup.selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LabelWithLoading } from './LabelWithLoading';
import { PopupDetailsWrapper } from './PopupDetails.styles';
import { ExtendedDetails } from './ExtendedDetails';
import { useDateFormatter } from './useDateFormatter';
import { Feature } from './types';
import { assetSchemaSelectorByType } from '@innovyze/lib_asset_schema_store';
import { AssetSchema } from '@innovyze/lib_asset_schema';

interface PopupDetailsProps {
	feature?: Feature;
}

const hasExtendedDetails = (assetSchema: AssetSchema | undefined) =>
	assetSchema?.hasCapability('Inspections') ?? false;

export const PopupDetails: React.VFC<PopupDetailsProps> = ({ feature }) => {
	const dispatch = useDispatch();
	const { t } = useGlobalization();
	const popupDetails = useSelector(popupSelectedAssetSelector);
	const assetSchema = useSelector(state =>
		popupDetails?.assetType
			? assetSchemaSelectorByType(state, popupDetails.assetType)
			: undefined,
	);

	const isLoading = !popupDetails;
	const areInspectionsLoading =
		useSelector(popupInspectionsLoadingSelector) ?? false;
	const formatDate = useDateFormatter();

	useEffect(() => {
		if (!feature) return;
		const [assetType, assetId] = feature.id.split('#');
		dispatch(
			getPopupDetails({
				assetId,
				assetType,
			}),
		);
	}, [feature]);

	return (
		<PopupDetailsWrapper>
			<LabelWithLoading
				label={t('Asset Id')}
				value={popupDetails?.assetId}
				loading={isLoading}
			/>
			<LabelWithLoading
				label={t('Asset Type')}
				value={popupDetails?.assetType}
				loading={isLoading}
			/>
			<LabelWithLoading
				label={t('Install Date')}
				value={formatDate(
					popupDetails?.yearLaid ||
						popupDetails?.yearInstalled ||
						popupDetails?.dateInstalled,
				)}
				loading={isLoading}
			/>
			{hasExtendedDetails(assetSchema) && (
				<ExtendedDetails
					popupDetails={popupDetails}
					isLoading={isLoading}
					areInspectionsLoading={areInspectionsLoading}
				/>
			)}
		</PopupDetailsWrapper>
	);
};
